import React from 'react';
import { NavLink } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faFutbol, faDice, faReceipt, faWallet, faUser, faUsers, faListOl,
  faRightToBracket, faHouse,
} from '@fortawesome/free-solid-svg-icons';
import { useTranslation } from 'react-i18next';
import { isPlayer, eOspite } from '../../utils/currentUser';
import { haLaVoce } from '../../utils/permessi';
import { frase } from '../../frase';
import FoglioGiochi from './FoglioGiochi';
import { sezioneAccesa } from '../../moduli/leggi';
import { useSkin } from '../../moduli/useSkin';
import './NavBar.css';

/**
 * La barra di navigazione del guscio.
 *
 * UNA sola barra, non due: sul telefono sta in basso, e sopra la soglia non
 * c'e' perche' in alto c'e' gia' tutto. Il passaggio lo decide il CSS con una
 * @media, non JavaScript che misura la finestra — misurare in JavaScript
 * vuol dire rimontare l'albero a ogni ridimensionamento e, al primo
 * disegno, far lampeggiare la versione sbagliata.
 *
 * Le voci dipendono da chi entra, e la fonte è il token: isPlayer(), eOspite()
 * e haLaVoce() (utils/permessi.js, le voci del sito) leggono di lì, come fa
 * già il resto del sito. Niente elenchi ruolo -> voci scritti qui dentro:
 * non potrebbero sapere che a un master sono stati tolti i giroconti.
 *
 * TRE CASI, non due.
 * Chi arriva senza conto finiva nel ramo degli operatori e si vedeva "Conto"
 * e "Profilo": due voci grosse che portano soltanto alla schermata di
 * accesso, cioe' un tocco speso per trovare una porta chiusa. Adesso l'ospite
 * ha il suo ramo — e dentro c'e' la SCHEDINA, perche' senza conto si puo'
 * benissimo scegliere le quote e vedere quanto viene: il conto della vincita
 * lo fa il browser. Il conto serve al momento di giocare, non prima.
 *
 * "Giochi" e "Schedina" non sono indirizzi: la prima apre un foglio, la
 * seconda la schedina che la pagina delle quote ha gia'. Il numero sulla
 * pastiglia arriva da userService: la barra sta nel guscio e dentro quella
 * pagina non vede.
 *
 * "Esci" non sta qui: sta dentro Profilo. Una voce che chiude la sessione
 * accanto a quelle che ci si naviga è un errore che capita da solo.
 */
export default function NavBar({ userService }) {
  const { t } = useTranslation();
  const [giochi, setGiochi] = React.useState(false);
  const [selezioni, setSelezioni] = React.useState(0);
  const [schedinaAperta, setSchedinaAperta] = React.useState(false);
  /* SENZA SPORT NIENTE SPORT E NIENTE SCHEDINA: su una skin «solo casino'»
     le due voci sparicono da sole, e la barra non resta con due porte
     chiuse. Si rilegge quando la skin cambia (anteprima). */
  useSkin();
  const conSport = sezioneAccesa('sport');

  /* Quante selezioni ci sono nella schedina. Lo dice la pagina delle quote;
     se il servizio non c'e' (montata altrove, o nel banco di prova) la
     pastiglia semplicemente non compare. */
  React.useEffect(() => {
    if (!userService || !userService.selezioni) return undefined;
    const sub = userService.selezioni().subscribe((n) => setSelezioni(n || 0));
    return () => sub.unsubscribe();
  }, [userService]);

  /* CHI SEI PUO' CAMBIARE MENTRE LA BARRA E' IN PIEDI.
     Le voci si ricavano dal token, e il token compare all'accesso e sparisce
     all'uscita: due cose che succedono fuori da React, che quindi non
     ridisegna niente. Risultato, provato sul telefono: dopo l'accesso la
     barra restava con "Entra" e senza Conto ne' Profilo, come se fossi
     ancora un ospite. Qui ci si mette in ascolto e si rilegge. */
  const [giro, rileggi] = React.useState(0);
  React.useEffect(() => {
    if (!userService || !userService.isLoggedIn) return undefined;
    const sub = userService.isLoggedIn().subscribe(() => rileggi((n) => n + 1));
    return () => sub.unsubscribe();
  }, [userService]);

  /* La schedina e' aperta o chiusa. La barra lo deve SAPERE, non solo
     ordinare: sul telefono la schedina prende tutta la schermata, e una voce
     che la sa solo aprire ti ci lascia dentro. */
  React.useEffect(() => {
    if (!userService || !userService.showTicketToggle) return undefined;
    const sub = userService.showTicketToggle()
        .subscribe((v) => setSchedinaAperta(!!v));
    return () => sub.unsubscribe();
  }, [userService]);

  const mostraSchedina = (v) => userService && userService.toggleTicket
    && userService.toggleTicket(v);
  const cambiaSchedina = () => mostraSchedina(!schedinaAperta);
  /* Tornare allo sport chiude la schedina: e' la stessa schermata, e
     lasciarla aperta sotto vorrebbe dire toccare Sport e non vedere lo
     sport. */
  const vaiAlloSport = () => mostraSchedina(false);
  const entra = () => userService && userService.richiediAccesso
    && userService.richiediAccesso();

  const vociOspite = [
    { to: '/', label: frase('Home'), icon: faHouse, esatto: true, quandoVai: vaiAlloSport },
    conSport && { to: '/sport', label: frase('Sport'), icon: faFutbol, quandoVai: vaiAlloSport },
    { azione: () => setGiochi(true), label: frase('Giochi'), icon: faDice },
    conSport && { azione: cambiaSchedina, label: frase('Schedina'), icon: faReceipt,
      conta: true, accesa: schedinaAperta },
    { azione: entra, label: frase('Accedi'), icon: faRightToBracket, invito: true },
  ].filter(Boolean);

  const vociGiocatore = [
    { to: '/', label: frase('Home'), icon: faHouse, esatto: true, quandoVai: vaiAlloSport },
    conSport && { to: '/sport', label: frase('Sport'), icon: faFutbol, quandoVai: vaiAlloSport },
    { azione: () => setGiochi(true), label: frase('Giochi'), icon: faDice },
    conSport && { azione: cambiaSchedina, label: frase('Schedina'), icon: faReceipt,
      conta: true, accesa: schedinaAperta },
    { to: '/my-account', label: frase('Conto'), icon: faWallet, esatto: true },
    haLaVoce('SITE_PROFILE_VIEW')
      && { to: '/my-account/profilo', label: frase('Profilo'), icon: faUser },
  ].filter(Boolean);

  const vociOperatore = [
    { to: '/', label: frase('Home'), icon: faHouse, esatto: true, quandoVai: vaiAlloSport },
    haLaVoce('SITE_NETWORK_VIEW')
      && { to: '/my-account/lista-utenti', label: frase('Rete'), icon: faUsers },
    haLaVoce('SITE_MOVEMENTS_VIEW')
      && { to: '/my-account/lista-movimenti', label: frase('Movimenti'), icon: faListOl },
    conSport && { to: '/sport', label: frase('Sport'), icon: faFutbol },
    { to: '/my-account', label: frase('Conto'), icon: faWallet, esatto: true },
    haLaVoce('SITE_PROFILE_VIEW')
      && { to: '/my-account/profilo', label: frase('Profilo'), icon: faUser },
  ].filter(Boolean);

  /* Si rilegge a ogni giro, cioe' a ogni accesso o uscita: scritto cosi' la
     dipendenza e' dichiarata e non c'e' modo di crederlo calcolato una
     volta sola. */
  const ospite = React.useMemo(() => eOspite(), [giro]);
  const giocatore = React.useMemo(() => isPlayer(), [giro]);
  const voci = ospite ? vociOspite
    : (giocatore ? vociGiocatore : vociOperatore);

  const dentro = (v) => (
    <>
      <span className="lvc-nav-segno">
        <FontAwesomeIcon icon={v.icon} className="lvc-nav-icona" />
        {v.conta && selezioni > 0
          && <span className="lvc-nav-pastiglia">{selezioni}</span>}
      </span>
      <span className="lvc-nav-testo">{t(v.label)}</span>
    </>
  );

  return (
    <>
      <nav className="lvc-nav" aria-label={t('Navigazione principale')}>
        {voci.map((v) => (v.to ? (
          <NavLink
            key={v.to}
            to={v.to}
            end={!!v.esatto}
            onClick={v.quandoVai}
            className={({ isActive }) => 'lvc-nav-voce'
              + (isActive && !schedinaAperta ? ' attiva' : '')}
          >
            {dentro(v)}
          </NavLink>
        ) : (
          <button
            key={v.label}
            type="button"
            onClick={v.azione}
            className={'lvc-nav-voce' + (v.invito ? ' invito' : '')
              + (v.accesa ? ' attiva' : '')}
          >
            {dentro(v)}
          </button>
        )))}
      </nav>

      <FoglioGiochi aperto={giochi} onChiudi={() => setGiochi(false)} />
    </>
  );
}
